import React, { memo, useContext } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import format from 'date-fns/format';
import formatDistanceToNow from 'date-fns/formatDistanceToNow';
import isValid from 'date-fns/isValid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircle,
  faExclamation,
  faExclamationCircle,
  faExclamationTriangle,
  faSquare,
} from '@fortawesome/free-solid-svg-icons';
import ReactTooltip from 'react-tooltip';

import { CORE, CORE_READ } from '../../Constants/RBAC';
import OrgsMapContext from '../../Context/OrgsMapContext';
import DM1FaultsConstants from '../../Lib/DM1FaultsConstants';
import Can from '../../RBAC/Can';
import j1939Dictionary from '../../Static/j1939Dictionary.json';

export default memo(DmAlertsTableBody);

const DM_CONSTANTS = new DM1FaultsConstants();
const ALERT_STATUS_CLASS_MAP = new Map([
  ['positive', 'text-active-dm'],
  ['negative', 'text-third-gray'],
]);
const LAMPS_ICON_MAP = new Map([
  ['AWL', SquareIcon],
  ['MIL:PL', TriangleIcon],
  ['RSL', CircleIcon],
]);

function getDisplayName(SPN, FMI) {
  const spnName = j1939Dictionary[SPN] ? j1939Dictionary[SPN].SPN_Name : SPN;
  return `${spnName} - ${DM_CONSTANTS.listFMIname[FMI]}`;
}

function getSourceDisplayName(SourceAddress) {
  return DM_CONSTANTS.listSourceAddress[SourceAddress] || 'Unknown SA';
}

function DmAlertsTableBody({ data, assetView }) {
  const orgsMap = useContext(OrgsMapContext);

  return data.map((dm) => {
    const {
      AWL,
      Canbus,
      FMI,
      GatewayId,
      GatewayName,
      Id,
      MIL,
      OC,
      OrgId,
      PL,
      RSL,
      SourceAddress,
      SPN,
      Transition,
      TriggeredAt,
    } = dm;
    const triggeredAtDate = new Date(TriggeredAt);
    const detectedAtDisplay = isValid(triggeredAtDate)
      ? format(triggeredAtDate, 'MMM d Y - h:mmaaa')
      : null;
    const durationDisplay =
      isValid(triggeredAtDate) && Transition === 'positive'
        ? `(${formatDistanceToNow(triggeredAtDate, { includeSeconds: true })})`
        : null;

    return (
      <tr key={Id}>
        <td className="align-middle">
          <FontAwesomeIcon
            icon={faCircle}
            size="sm"
            className={`${ALERT_STATUS_CLASS_MAP.get(Transition)} ml-3`}
          />
        </td>
        {assetView ? null : (
          <td className="align-middle">
            <Can
              no={() => (
                <button
                  className="btn btn-link p-0 three-quarter-text"
                  disabled
                >
                  {GatewayName}
                </button>
              )}
              orgID={OrgId}
              perform={CORE_READ}
              resource={CORE}
              yes={() => (
                <Link
                  to={{
                    pathname: `/asset/${GatewayId}`,
                    search: '?tab=dashboard',
                  }}
                  replace={true}
                  className="three-quarter-text"
                >
                  {GatewayName}
                </Link>
              )}
            />
          </td>
        )}
        <td className="three-quarter-text align-middle">
          {getDisplayName(SPN, FMI)}
        </td>
        {assetView ? null : (
          <td className="align-middle three-quarter-text">
            {orgsMap?.[OrgId]?.name}
          </td>
        )}
        <td className="align-middle text-center">
          {[
            [[AWL], 'AWL'],
            [[MIL, PL], 'MIL:PL'],
            [[RSL], 'RSL'],
          ].map(([lamps, iconKey], i) => {
            if (!lamps.includes(1)) return null;

            let tooltip;
            if (lamps.length === 1) {
              tooltip = `Active ${iconKey}`;
            } else {
              const lampNames = iconKey.split(':');
              tooltip = lamps.reduce((str, lamp, index) => {
                if (lamp !== 0) return str;
                return `${str} ${lampNames[index]}`;
              }, 'Active');
            }

            const Icon = LAMPS_ICON_MAP.get(iconKey);
            return (
              <div
                data-for={`${Id}-${i}`}
                data-tip={tooltip}
                key={`${Id}-${i}`}
              >
                <Icon />
                <ReactTooltip
                  arrowColor="#fff"
                  className="bg-white shadow rounded-lg text-dark three-quarter-text"
                  id={`${Id}-${i}`}
                  multiline
                />
              </div>
            );
          })}
        </td>
        <td className="three-quarter-text align-middle">
          <div className="mb-0">{detectedAtDisplay}</div>
          <div className="mb-0">{durationDisplay}</div>
        </td>
        <td className="three-quarter-text align-middle">{SPN}</td>
        <td className="three-quarter-text align-middle">{FMI}</td>
        <td className="three-quarter-text align-middle">
          {getSourceDisplayName(SourceAddress)}
        </td>
        <td className="three-quarter-text align-middle text-center">
          {Canbus}
        </td>
        <td className="three-quarter-text align-middle text-center">{OC}</td>
      </tr>
    );
  });
}

DmAlertsTableBody.propTypes = {
  assetView: PropTypes.bool,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      AWL: PropTypes.number,
      Canbus: PropTypes.number,
      CreatedAt: PropTypes.string,
      DetectedAt: PropTypes.string,
      FMI: PropTypes.number,
      GatewayId: PropTypes.string,
      GatewayName: PropTypes.string,
      Id: PropTypes.string,
      MIL: PropTypes.number,
      OC: PropTypes.number,
      OrgId: PropTypes.string,
      PL: PropTypes.number,
      RSL: PropTypes.number,
      SPN: PropTypes.number,
      SourceAddress: PropTypes.number,
      Transition: PropTypes.string,
      TriggeredAt: PropTypes.string,
      UpdatedAt: PropTypes.string,
    })
  ),
};

DmAlertsTableBody.defaultProps = { data: [] };

function CircleIcon() {
  return (
    <FontAwesomeIcon
      className="text-active-dm my-1"
      icon={faExclamationCircle}
    />
  );
}

function SquareIcon() {
  return (
    <span className="fa-layers fa-fw my-1">
      <FontAwesomeIcon className="text-alert-orange" icon={faSquare} />
      <FontAwesomeIcon
        color="white"
        icon={faExclamation}
        transform="shrink-6"
      />
    </span>
  );
}

function TriangleIcon() {
  return (
    <FontAwesomeIcon
      className="text-warning-yellow my-1"
      icon={faExclamationTriangle}
    />
  );
}
